import React, { useEffect, useMemo } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
   PageHeader,
   PageTitle,
   Wrapper as VendoWrapper,
   useVendoData,
} from '@/pages/Admin/Vendo';
import { useVendoDevicesStore } from '@/store/useVendoDevicesStore';
import { colors } from '@/constants/colors';
import { useFocusFromQuery } from '@/hooks/useFocusFromQuery';

/**
 * /wifi-sold-device — read-only audit page for devices the operator has
 * transferred to other owners. The transfer is already accepted, so
 * isCurrentOwner is false and the device no longer counts toward any
 * online/finance aggregations on the main Devices page.
 *
 * Source of truth is the same useVendoDevicesStore the main page uses:
 * listDevices on the server returns both currently-owned and previously-
 * sold devices (the latter with soldTransfer populated), so all data is
 * already in memory by the time the operator clicks through.
 */
const SoldDevices: React.FC = () => {
   useFocusFromQuery('device');
   useVendoData({ sessions: false });
   const navigate = useNavigate();
   const apiDevices = useVendoDevicesStore((s) => s.devices);
   const fetched = useVendoDevicesStore((s) => s.fetched);
   const loading = useVendoDevicesStore((s) => s.loading);
   const fetch = useVendoDevicesStore((s) => s.fetch);

   useEffect(() => {
      if (!fetched && !loading) {
         fetch();
      }
   }, [fetched, loading, fetch]);

   const sold = useMemo(
      () =>
         apiDevices
            .filter((d) => d.isCurrentOwner === false && d.soldTransfer)
            .sort((a, b) => {
               const aAt = a.soldTransfer?.acceptedAt
                  ? new Date(a.soldTransfer.acceptedAt).getTime()
                  : 0;
               const bAt = b.soldTransfer?.acceptedAt
                  ? new Date(b.soldTransfer.acceptedAt).getTime()
                  : 0;
               return bAt - aAt;
            }),
      [apiDevices]
   );

   const stats = useMemo(() => {
      const paidSales = sold.filter(
         (d) => typeof d.soldTransfer?.fullCashAmount === 'number'
      );
      const plainSales = sold.filter(
         (d) => !d.soldTransfer?.fullCashAmount
      );
      const totalRevenue = paidSales.reduce(
         (sum, d) => sum + (d.soldTransfer?.fullCashAmount ?? 0),
         0
      );
      const latest = sold[0]?.soldTransfer?.acceptedAt;
      return {
         total: sold.length,
         paid: paidSales.length,
         plain: plainSales.length,
         revenue: totalRevenue,
         latest: latest ? new Date(latest) : null,
      };
   }, [sold]);

   return (
      <VendoWrapper>
         <PageHeader>
            <PageTitle>Sold devices</PageTitle>
            <Spacer />
            <BackBtn onClick={() => navigate('/wifi-devices')}>
               ← Back to Devices
            </BackBtn>
         </PageHeader>

         <Subhead>
            Devices you transferred to other SmartFi operators. They no longer
            count toward your online or revenue totals — kept here for the
            audit trail.
         </Subhead>

         <StatsGrid>
            <Stat>
               <StatLabel>Total sold</StatLabel>
               <StatValue $color={colors.PRIMARY}>{stats.total}</StatValue>
            </Stat>
            <Stat>
               <StatLabel>Paid sales</StatLabel>
               <StatValue $color={colors.SUCCESS}>{stats.paid}</StatValue>
            </Stat>
            <Stat>
               <StatLabel>Plain transfers</StatLabel>
               <StatValue $color={colors.BLUE}>{stats.plain}</StatValue>
            </Stat>
            <Stat>
               <StatLabel>Total revenue</StatLabel>
               <StatValue $color={colors.PURPLE}>
                  ₱{stats.revenue.toLocaleString()}
               </StatValue>
            </Stat>
         </StatsGrid>

         {loading && !fetched ? (
            <SkeletonLines lines={3} />
         ) : sold.length === 0 ? (
            <EmptyNote>
               <EmptyIcon>📦</EmptyIcon>
               <EmptyTitle>No sold devices yet</EmptyTitle>
               <EmptyBody>
                  When a transfer is accepted by the buyer, the device moves
                  here for the audit trail.
               </EmptyBody>
            </EmptyNote>
         ) : (
            <Table>
               <THead>
                  <TR>
                     <TH>Device</TH>
                     <TH>Sold to</TH>
                     <TH>Type</TH>
                     <TH align="right">Amount</TH>
                     <TH>Sold on</TH>
                  </TR>
               </THead>
               <TBody>
                  {sold.map((d) => {
                     const acceptedAt = d.soldTransfer?.acceptedAt
                        ? new Date(d.soldTransfer.acceptedAt)
                        : null;
                     const amount = d.soldTransfer?.fullCashAmount;
                     return (
                        <TR key={d._id} data-focus-id={d._id}>
                           <TD>
                              <DeviceName>{d.name}</DeviceName>
                              <DeviceMeta>{d.deviceId}</DeviceMeta>
                           </TD>
                           <TD>{d.soldTransfer?.toEmail ?? '—'}</TD>
                           <TD>
                              <Badge $paid={!!amount}>
                                 {amount ? 'Paid' : 'Plain Transfer'}
                              </Badge>
                           </TD>
                           <TD align="right">
                              {amount ? `₱${amount.toLocaleString()}` : '—'}
                           </TD>
                           <TD>
                              {acceptedAt
                                 ? acceptedAt.toLocaleDateString()
                                 : '—'}
                           </TD>
                        </TR>
                     );
                  })}
               </TBody>
            </Table>
         )}
      </VendoWrapper>
   );
};

export default SoldDevices;

const Spacer = styled.div`
   flex: 1;
`;

const BackBtn = styled.button`
   background: transparent;
   color: ${colors.PRIMARY};
   border: 1px solid ${colors.PRIMARY}40;
   border-radius: 8px;
   padding: 8px 14px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   &:hover {
      background: ${colors.PRIMARY}10;
   }
`;

const Subhead = styled.p`
   color: ${colors.GRAY3};
   font-size: 13px;
   margin: -4px 0 20px;
   max-width: 720px;
   line-height: 1.5;
`;

const StatsGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(4, 1fr);
   gap: 12px;
   margin-bottom: 24px;
   @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
   }
`;

const Stat = styled.div`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   padding: 14px 16px;
`;

const StatLabel = styled.div`
   font-size: 11px;
   font-weight: 700;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;

const StatValue = styled.div<{ $color: string }>`
   font-size: 22px;
   font-weight: 700;
   color: ${({ $color }) => $color};
   margin-top: 6px;
`;

const Table = styled.table`
   width: 100%;
   border-collapse: collapse;
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 12px;
   overflow: hidden;
   font-size: 13px;
`;

const THead = styled.thead`
   background: ${colors.GRAY14};
`;

const TBody = styled.tbody``;

const TR = styled.tr`
   border-bottom: 1px solid ${colors.GRAY12};
   &:last-child {
      border-bottom: none;
   }
`;

const TH = styled.th<{ align?: 'left' | 'right' }>`
   text-align: ${({ align }) => align ?? 'left'};
   font-size: 11px;
   font-weight: 700;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.04em;
   padding: 12px 16px;
`;

const TD = styled.td<{ align?: 'left' | 'right' }>`
   text-align: ${({ align }) => align ?? 'left'};
   padding: 14px 16px;
   color: ${colors.GRAY_DARK};
   vertical-align: middle;
`;

const DeviceName = styled.div`
   font-weight: 700;
   font-size: 13px;
`;

const DeviceMeta = styled.div`
   font-family: 'SF Mono', Menlo, monospace;
   font-size: 10px;
   color: ${colors.GRAY5};
   margin-top: 2px;
`;

const Badge = styled.span<{ $paid: boolean }>`
   padding: 3px 10px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 700;
   background: ${({ $paid }) =>
      $paid ? `${colors.SUCCESS}1f` : `${colors.PRIMARY}15`};
   color: ${({ $paid }) => ($paid ? colors.SUCCESS : colors.PRIMARY)};
`;

const EmptyNote = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 8px;
   padding: 48px 24px;
   color: ${colors.GRAY3};
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 12px;
   text-align: center;
`;

const EmptyIcon = styled.div`
   font-size: 32px;
`;

const EmptyTitle = styled.div`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.GRAY_DARK};
`;

const EmptyBody = styled.div`
   font-size: 12px;
   max-width: 360px;
   line-height: 1.5;
`;
